import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithElementSearchAndFilters,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_VIEW,
  type PropertiesConfig,
  type PropertiesConfigInputOptionsToggle,
  SearchAndFiltersMode,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { GanttChartComponent } from './gantt-chart.component';
import {
  GanttChartDay,
  type GanttChartPropsModel,
  GanttChartTimeScale,
} from './gantt-chart.models';
import { GanttChartPropsBuilder } from './gantt-chart.props-builder';
import { GanttChartPropsExtractor } from './gantt-chart.props-extractor';
import { ganttChartV2Migration } from './migrations/002-gantt-chart.migration';

export type GanttChartDef = WithProps<GanttChartPropsModel> &
  WithElementSearchAndFilters &
  WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    ganttChart: GanttChartDef;
  }
}

// dimensions must fit the placeholder
const DEFAULT_WIDTH = 888;
const DEFAULT_HEIGHT = 200;

registerElementDefinition({
  type: 'ganttChart',
  componentType: GanttChartComponent,
  isListed: true,
  name: 'Gantt Chart',
  width: { mode: 'full', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: GanttChartPropsBuilder,
  propsExtractor: GanttChartPropsExtractor,
  defaults: getDefaults(),
  initial: getInitial(),
  tags: ['Table', 'chart', 'list', 'timeline'],
  groups: ['charts'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  metaUiFeatures: {
    type: 'container',
    paths: {
      columns: 'content.customFields',
    },
  },
  features: {
    addRelatedWidgetSupport: true,
    loadingIndication: 'fadeWithIndicator',
    visualStyle: 'block',
  },
  version: 2,
  migrations: {
    2: ganttChartV2Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'row-select', schema: DEFAULT_SCHEMA }, // schema set in gantt-chart.component
      { eventType: 'action', schema: DEFAULT_SCHEMA },
    ],
  };
}

function getDefaults(): PropsOf<'ganttChart'> {
  return {
    content: {
      columns: [],
      fields: {},
      customFields: [],
      searchAndFilters: {
        mode: SearchAndFiltersMode.PREDEFINED,
        filters: [],
      },
      itemAdd: { enabled: false, label: '' },
      itemView: { enabled: false },
      itemEdit: { enabled: false },
      itemDelete: { enabled: false },
      visible: undefined,
      title: {
        enabled: false,
        label: 'Gantt Chart',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      options: {
        timeScale: GanttChartTimeScale.Month,
        startWeekOn: GanttChartDay.Monday,
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      title: {
        text: { color1: '$t.title', selectorType: 'solid' },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      headers: {
        text: { color1: '$t.title', selectorType: 'solid' },
        background: { color1: '$t.complementary', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        width: undefined,
      },
      table: {
        text: { color1: '$t.value', selectorType: 'solid' },
        background: { color1: '$t.fillInternal', selectorType: 'solid' },
        lines: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
        hideColumnsBorder: false,
      },
      bar: {
        palette: {
          paletteName: 'palette2',
          foreground: '$t.value',
          background: '$t.outline',
        },
      },
      container: {
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'ganttChart'> {
  return {
    content: {
      searchAndFilters: { injectToElement: true },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('ganttChart', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Data',
        [],
        [
          epc.fields.selectDataSourceField({
            key: 'value',
            label: 'Data source',
            sources: 'list',
            bindings: [],
          }),
          ...epc.utils.disabledFields(
            [
              epc.fields.listFieldsField('content.columns', 'data.?key:value.dataSource'),
              epc.fields.selectFieldFromModel(
                'content.fields.category',
                'Category',
                'content.columns',
                {
                  showSelection: true,
                  allowNone: true,
                  labelField: 'category',
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                  placeholder: 'Optional',
                },
              ),
              epc.fields.selectFieldFromModel('content.fields.task', 'Task', 'content.columns', {
                showSelection: true,
                allowNone: true,
                labelField: 'name',
                valueField: COLUMN_ID_OR_NAME,
                optionsFilter: epc.utils.columnFormatFilter('!secureText'),
              }),
              epc.fields.selectFieldFromModel(
                'content.fields.startDate',
                'Start Date',
                'content.columns',
                {
                  showSelection: true,
                  allowNone: true,
                  labelField: 'name',
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('date', 'datetime'),
                },
              ),
              epc.fields.selectFieldFromModel(
                'content.fields.endDate',
                'End Date',
                'content.columns',
                {
                  showSelection: true,
                  allowNone: true,
                  labelField: 'name',
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('date', 'datetime'),
                },
              ),
              epc.utils.separatedField(
                epc.fields.listCustomFieldsField('content.customFields', 'Add Custom Fields', {
                  withLabelPosition: false,
                }),
              ),
            ],
            (model) =>
              !getValueByPath(model, 'data.?key:value.dataSource')
                ? 'The Value of the element is not bound'
                : undefined,
          ),
        ],
        { enabledByDefault: true },
      ),

      epc.subsections.subsection('options', 'Options', [
        epc.fields.selectFieldFromList(
          'content.options.timeScale',
          'Time scale',
          [
            {
              label: 'Daily',
              value: GanttChartTimeScale.Day,
            },
            {
              label: 'Weekly',
              value: GanttChartTimeScale.Week,
            },
            {
              label: 'Monthly',
              value: GanttChartTimeScale.Month,
            },
          ],
          { showSelection: true, allowNone: false },
        ),
        epc.utils.conditionalField(
          epc.fields.selectFieldFromList(
            'content.options.startWeekOn',
            'Start week on',
            [
              { label: 'Monday', value: GanttChartDay.Monday },
              { label: 'Saturday', value: GanttChartDay.Saturday },
              { label: 'Sunday', value: GanttChartDay.Sunday },
            ],
            { showSelection: true, allowNone: false },
          ),
          'content.options.timeScale',
          (timeScale: GanttChartTimeScale) => timeScale !== GanttChartTimeScale.Week,
        ),
      ]),

      epc.subsections.searchAndFiltersSubsection(),

      epc.subsections.subsection('content', 'Actions', [
        ...epc.utils.disabledFields(
          [
            epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
              epc.fields.toggleField('content.itemView.enabled', 'Details on click'),
              {
                action: () => ({
                  id: ITEMS_CONTAINER_ACTION_VIEW,
                  styled: { enabled: true, elementType: 'button' },
                }),
              },
            ),
            epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
              epc.fields.toggleField('content.itemAdd.enabled', 'Add new row'),
              {
                action: () => ({
                  id: ITEMS_CONTAINER_ACTION_ADD,
                  styled: { enabled: true, elementType: 'button' },
                }),
              },
            ),
            epc.utils.conditionalField(
              epc.fields.textInput('content.itemAdd.label', 'Add label'),
              'content.itemAdd.enabled',
              (value) => !value,
            ),
            epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
              epc.fields.toggleField('content.itemEdit.enabled', 'Edit row'),
              {
                action: () => ({
                  id: ITEMS_CONTAINER_ACTION_EDIT,
                  styled: { enabled: true, elementType: 'button' },
                }),
              },
            ),
          ],

          (model: unknown) =>
            !getValueByPath(model, 'data.?key:value.dataSource')
              ? 'The Value of the element is not bound'
              : undefined,
        ),
      ]),

      epc.subsections.visibilitySubsection(),

      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
          ...epc.fields.visualEffectField('style.general.visualEffect'),
        ]),
        epc.subsections.subsection('style.headers', 'Column Header and Category', [
          epc.fields.colorField('style.headers.text', 'Label', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.headers.background', 'Background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.headers.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),
        epc.subsections.subsection('style.table', 'Table', [
          epc.fields.colorField('style.table.text', 'Text', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.table.background', 'Background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.table.lines', 'Lines', { allowGradient: false }),
          epc.fields.radiusField('style.table.radius', 'Table radius'),
          epc.fields.toggleField('style.table.hideColumnsBorder', 'Hide column border'),
        ]),
        epc.subsections.subsection('style.bar', 'Time Bar', [
          epc.fields.tagsColorPaletteField('style.bar.palette', 'Palette', { hideLabel: true }),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.title.text',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
